import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import LaTeXRenderer from './LaTeXRenderer';

function EditQuestionModal({ question, open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [formData, setFormData] = useState({
    question_type: '',
    question_text: '',
    choices: [],
    correct_answer: '',
    explanation: '',
    difficulty: 'medium',
    tags: [],
    points: 1
  });

  useEffect(() => {
    if (question && open) {
      setFormData({
        question_type: question.question_type || 'mcq_single',
        question_text: question.question_text || '',
        choices: question.choices || [],
        correct_answer: question.correct_answer || '',
        explanation: question.explanation || '',
        difficulty: question.difficulty || 'medium',
        tags: question.tags || [],
        points: question.points || 1
      });
      fetchUsage();
    }
  }, [question, open]);

  const fetchUsage = async () => {
    try {
      const response = await api.get(`/questions/${question.id}/usage`);
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put(`/questions/${question.id}`, {
        bank_id: question.bank_id,
        ...formData
      });
      toast.success('Question updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setLoading(false);
    }
  };

  const updateChoices = (index, value) => {
    const newChoices = [...formData.choices];
    newChoices[index] = value;
    setFormData({ ...formData, choices: newChoices });
  };

  const addChoice = () => {
    setFormData({ ...formData, choices: [...formData.choices, ''] });
  };

  const removeChoice = (index) => {
    const newChoices = formData.choices.filter((_, i) => i !== index);
    setFormData({ ...formData, choices: newChoices });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Question</DialogTitle>
        </DialogHeader>

        {usage && usage.published_tests > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Warning: Question in use</p>
              <p>This question is used in {usage.published_tests} published test(s). Changes will affect all tests.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Question Type</Label>
            <Select
              value={formData.question_type || 'mcq_single'}
              onValueChange={(value) => setFormData({ ...formData, question_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mcq_single">Multiple Choice (Single)</SelectItem>
                <SelectItem value="mcq_multiple">Multiple Choice (Multiple)</SelectItem>
                <SelectItem value="true_false">True/False</SelectItem>
                <SelectItem value="fill_blank">Fill in the Blank</SelectItem>
                <SelectItem value="short_answer">Short Answer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Question Text *</Label>
            <Textarea
              value={formData.question_text}
              onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
              required
              rows={3}
            />
          </div>

          {(formData.question_type === 'mcq_single' || formData.question_type === 'mcq_multiple' || formData.question_type === 'true_false') && (
            <div className="space-y-2">
              <Label>Choices</Label>
              {formData.choices.map((choice, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={choice}
                    onChange={(e) => updateChoices(index, e.target.value)}
                    placeholder={`Choice ${index + 1}`}
                    required
                  />
                  {formData.choices.length > 2 && (
                    <Button type="button" variant="outline" onClick={() => removeChoice(index)}>
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              {formData.question_type !== 'true_false' && (
                <Button type="button" variant="outline" onClick={addChoice}>
                  Add Choice
                </Button>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Correct Answer *</Label>
            <Input
              value={formData.correct_answer}
              onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Explanation (Optional)</Label>
            <Textarea
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Difficulty</Label>
              <Select
                value={formData.difficulty || 'medium'}
                onValueChange={(value) => setFormData({ ...formData, difficulty: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Points</Label>
              <Input
                type="number"
                min="1"
                value={formData.points}
                onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
                required
              />
            </div>
          </div>

          {/* LaTeX Preview */}
          {formData.question_text && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">LaTeX Preview:</p>
              <div className="text-gray-900">
                <LaTeXRenderer text={formData.question_text} />
              </div>
              {formData.choices.length > 0 && formData.choices.some(c => c.trim()) && (
                <div className="mt-3 space-y-1">
                  <p className="text-sm text-gray-600">Choices:</p>
                  {formData.choices.map((choice, idx) => choice.trim() && (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <span className="font-medium text-gray-600">{String.fromCharCode(65 + idx)}.</span>
                      <LaTeXRenderer text={choice} />
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-blue-600 mt-2">
                Tip: Use $formula$ for inline math and $$formula$$ for display math
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Question'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditQuestionModal;
